import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getFirebaseToken } from "../utils/getFirebaseToken";
import {
  createSupplierItem,
  fetchSupplierItems,
  setPreferredSupplierItem,
  updateSupplierItem,
} from "../services/erpSupplierItems";
import {
  fetchPartners,
  fetchResourceItems,
  fetchSupplierSources,
} from "../services/erpMasterData";

function emptyForm() {
  return {
    item_id: "",
    partner_id: "",
    supplier_source_id: "",
    supplier_sku: "",
    supplier_name: "",
    supplier_description: "",
    supplier_uom: "",
    source_table: "",
    source_row_id: "",
    source_url: "",
    lead_time_days: 0,
    min_order_qty: 0,
    order_multiple: 0,
    availability_text: "",
    is_preferred: 0,
    is_active: 1,
    remarks: "",
    last_seen_at: "",
  };
}

function toDateTimeInputValue(value) {
  if (!value) return "";
  return String(value).slice(0, 16);
}

export default function SupplierItemsPage() {
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);

  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [preferredOnly, setPreferredOnly] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);

  const load = async () => {
  setLoading(true);
  setError("");
  try {
    const token = await getFirebaseToken();

    const [supplierItemsRes, partnersRes, itemsRes, sourcesRes] = await Promise.all([
      fetchSupplierItems(token, {
        q,
        partner_id: partnerFilter || undefined,
        supplier_source_id: sourceFilter || undefined,
        is_preferred: preferredOnly ? 1 : undefined,
        is_active: activeOnly ? 1 : undefined,
      }),
      fetchPartners(token),
      fetchResourceItems(token),
      fetchSupplierSources(token),
    ]);

    setRows(supplierItemsRes.rows || []);
    setPartners(partnersRes.rows || []);
    setItems(itemsRes.rows || []);
    setSources(sourcesRes.rows || []);
  } catch (e) {
    console.error(e);
    setError(
      e?.response?.data?.message || e.message || "Failed to load supplier items."
    );
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    load();
  }, []);

  const itemOptions = useMemo(() => {
    return items.map((x) => ({
      value: x.id,
      label: `${x.sku} - ${x.name}`,
    }));
  }, [items]);

  const partnerOptions = useMemo(() => {
    return partners.map((x) => ({
      value: x.id,
      label: `${x.code} - ${x.name}`,
    }));
  }, [partners]);

  const save = async () => {
    setSaving(true);
    setError("");

    try {
      const token = await getFirebaseToken();

      if (!form.item_id) throw new Error("Internal item is required.");
      if (!form.supplier_source_id) throw new Error("Supplier source is required.");
      if (!form.supplier_sku) throw new Error("Supplier SKU is required.");

      const payload = {
        ...form,
        item_id: form.item_id ? Number(form.item_id) : null,
        partner_id: form.partner_id ? Number(form.partner_id) : null,
        supplier_source_id: form.supplier_source_id
          ? Number(form.supplier_source_id)
          : null,
        source_row_id: form.source_row_id ? Number(form.source_row_id) : null,
        lead_time_days: Number(form.lead_time_days || 0),
        min_order_qty: Number(form.min_order_qty || 0),
        order_multiple: Number(form.order_multiple || 0),
        is_preferred: Number(form.is_preferred || 0),
        is_active: Number(form.is_active ?? 1),
        last_seen_at: form.last_seen_at
          ? `${form.last_seen_at}:00`
          : null,
      };

      if (editingId) {
        await updateSupplierItem(token, editingId, payload);
      } else {
        await createSupplierItem(token, payload);
      }

      setForm(emptyForm());
      setEditingId(null);
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      item_id: row.item_id || "",
      partner_id: row.partner_id || "",
      supplier_source_id: row.supplier_source_id || "",
      supplier_sku: row.supplier_sku || "",
      supplier_name: row.supplier_name || "",
      supplier_description: row.supplier_description || "",
      supplier_uom: row.supplier_uom || "",
      source_table: row.source_table || "",
      source_row_id: row.source_row_id || "",
      source_url: row.source_url || "",
      lead_time_days: row.lead_time_days || 0,
      min_order_qty: row.min_order_qty || 0,
      order_multiple: row.order_multiple || 0,
      availability_text: row.availability_text || "",
      is_preferred: row.is_preferred ? 1 : 0,
      is_active: row.is_active ? 1 : 0,
      remarks: row.remarks || "",
      last_seen_at: toDateTimeInputValue(row.last_seen_at),
    });
  };

  const handleSetPreferred = async (row) => {
    try {
      const token = await getFirebaseToken();
      await setPreferredSupplierItem(token, row.id);
      await load();
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to set preferred."
      );
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Supplier Mapping</h1>
        <p className="text-slate-600">
          Map internal items to supplier SKUs, sources and pricing logic.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-4 space-y-4">
        <div className="grid gap-3 md:grid-cols-4">
          <select
            value={form.item_id}
            onChange={(e) => setForm({ ...form, item_id: e.target.value })}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">Select internal item</option>
            {itemOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={form.partner_id}
            onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">Select supplier</option>
            {partnerOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={form.supplier_source_id}
            onChange={(e) =>
              setForm({ ...form, supplier_source_id: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          >
            <option value="">Select source</option>
            {sources.map((src) => (
              <option key={src.id} value={src.id}>
                {src.code} - {src.name}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Supplier SKU"
            value={form.supplier_sku}
            onChange={(e) => setForm({ ...form, supplier_sku: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-4">
          <input
            type="text"
            placeholder="Supplier Name"
            value={form.supplier_name}
            onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="text"
            placeholder="Supplier UOM"
            value={form.supplier_uom}
            onChange={(e) => setForm({ ...form, supplier_uom: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="number"
            placeholder="Lead time days"
            value={form.lead_time_days}
            onChange={(e) =>
              setForm({ ...form, lead_time_days: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="number"
            step="0.0001"
            placeholder="MOQ"
            value={form.min_order_qty}
            onChange={(e) =>
              setForm({ ...form, min_order_qty: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-4">
          <input
            type="number"
            step="0.0001"
            placeholder="Order multiple"
            value={form.order_multiple}
            onChange={(e) =>
              setForm({ ...form, order_multiple: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="text"
            placeholder="Availability text"
            value={form.availability_text}
            onChange={(e) =>
              setForm({ ...form, availability_text: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="text"
            placeholder="Source table"
            value={form.source_table}
            onChange={(e) => setForm({ ...form, source_table: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="number"
            placeholder="Source row ID"
            value={form.source_row_id}
            onChange={(e) => setForm({ ...form, source_row_id: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <input
            type="url"
            placeholder="Source URL"
            value={form.source_url}
            onChange={(e) => setForm({ ...form, source_url: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="datetime-local"
            value={form.last_seen_at}
            onChange={(e) => setForm({ ...form, last_seen_at: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="text"
            placeholder="Remarks"
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />
        </div>

        <div className="flex gap-6 items-center">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(form.is_preferred)}
              onChange={(e) =>
                setForm({ ...form, is_preferred: e.target.checked ? 1 : 0 })
              }
            />
            Preferred
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(form.is_active)}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked ? 1 : 0 })
              }
            />
            Active
          </label>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-black text-white"
          >
            {saving ? "Saving..." : editingId ? "Update Mapping" : "Create Mapping"}
          </button>

          {editingId ? (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(emptyForm());
              }}
              className="px-4 py-2 rounded-lg border"
            >
              Cancel Edit
            </button>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-4 space-y-4">
        <div className="grid gap-3 md:grid-cols-5">
          <input
            type="text"
            placeholder="Search internal/supplier SKU or name..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="border rounded-lg px-3 py-2"
          />

          <select
            value={partnerFilter}
            onChange={(e) => setPartnerFilter(e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">All suppliers</option>
            {partnerOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">All sources</option>
            {sources.map((src) => (
              <option key={src.id} value={src.id}>
                {src.code} - {src.name}
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2 px-2">
            <input
              type="checkbox"
              checked={preferredOnly}
              onChange={(e) => setPreferredOnly(e.target.checked)}
            />
            Preferred only
          </label>

          <label className="flex items-center gap-2 px-2">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            Active only
          </label>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            className="px-4 py-2 rounded-lg bg-black text-white"
          >
            Search
          </button>

          <button
            type="button"
            onClick={() => {
              setQ("");
              setPartnerFilter("");
              setSourceFilter("");
              setPreferredOnly(false);
              setActiveOnly(true);
            }}
            className="px-4 py-2 rounded-lg border"
          >
            Clear
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      ) : null}

      <div className="bg-white rounded-xl shadow-sm border overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-3 py-3 text-left">Internal Item</th>
              <th className="px-3 py-3 text-left">Supplier</th>
              <th className="px-3 py-3 text-left">Source</th>
              <th className="px-3 py-3 text-left">Supplier SKU</th>
              <th className="px-3 py-3 text-left">Supplier Name</th>
              <th className="px-3 py-3 text-right">Current Net Price</th>
              <th className="px-3 py-3 text-left">Currency</th>
              <th className="px-3 py-3 text-right">Lead Time</th>
              <th className="px-3 py-3 text-right">MOQ</th>
              <th className="px-3 py-3 text-left">Flags</th>
              <th className="px-3 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={11} className="px-4 py-6 text-center text-slate-500">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-6 text-center text-slate-500">
                  No supplier mappings found.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-3">
                    <div className="font-medium">{row.item_sku}</div>
                    <div className="text-slate-500">{row.item_name}</div>
                  </td>
                  <td className="px-3 py-3">
                    {row.partner_code ? `${row.partner_code} - ` : ""}
                    {row.partner_name || "-"}
                  </td>
                  <td className="px-3 py-3">
                    {row.source_code ? `${row.source_code} - ` : ""}
                    {row.source_name || "-"}
                  </td>
                  <td className="px-3 py-3">{row.supplier_sku}</td>
                  <td className="px-3 py-3">{row.supplier_name || "-"}</td>
                  <td className="px-3 py-3 text-right">
                    {Number(row.current_net_price || 0).toFixed(4)}
                  </td>
                  <td className="px-3 py-3">{row.current_currency || "-"}</td>
                  <td className="px-3 py-3 text-right">{row.lead_time_days || 0}</td>
                  <td className="px-3 py-3 text-right">
                    {Number(row.min_order_qty || 0).toFixed(4)}
                  </td>
                  <td className="px-3 py-3">
                    <div>{row.is_preferred ? "Preferred" : ""}</div>
                    <div>{row.is_active ? "Active" : "Inactive"}</div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(row)}
                        className="px-3 py-1.5 rounded-lg border"
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSetPreferred(row)}
                        className="px-3 py-1.5 rounded-lg border"
                      >
                        Set Preferred
                      </button>

                      <Link
                        to={`/erp/supplier-items/${row.id}/prices`}
                        className="px-3 py-1.5 rounded-lg border"
                      >
                        Prices
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}